import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, ArrowRight, MapPin, Users, HandCoins,
  Shield, ChevronRight, Heart, Building2, Globe, Star,
  Zap, Lock, FileText, TrendingUp, Mail, Phone, Calculator, Coffee,
  Calendar, Loader2, Megaphone, ZoomIn, Menu, X, ChevronDown, Check,
} from "lucide-react";
import TeroumaLogo from "../components/ui/TeroumaLogo";
import { apiClient } from "../lib/api";
import { chiffresCarte } from "../lib/etatCampagne";
import CampaignImageLightbox from "../components/shared/CampaignImageLightbox";
import HeroScrollFrames from "../components/landing/HeroScrollFrames";

/* ─── Animated counter ─────────────────────────────────────────────────── */
function Counter({ target, suffix = "", prefix = "", duration = 2200 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  // 🐛 Bug fix : avant on utilisait un useRef "started" qui restait à true
  // après la 1re animation → quand target arrivait en async (API), useEffect
  // se re-rendait mais l'animation ne se relançait jamais → val bloqué à 0
  // → la landing affichait "0 organisations" alors qu'il y en avait 1.
  // Maintenant l'animation se relance à chaque changement de target.
  useEffect(() => {
    if (!target && target !== 0) return;
    let cancelled = false;
    let obs = null;

    const animate = () => {
      const t0 = Date.now();
      const tick = () => {
        if (cancelled) return;
        const p = Math.min(1, (Date.now() - t0) / duration);
        setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if (ref.current) {
      obs = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) {
          animate();
          obs?.disconnect();
        }
      }, { threshold: 0.3 });
      obs.observe(ref.current);
    } else {
      // Element pas encore monté → on anime quand même direct
      animate();
    }

    return () => {
      cancelled = true;
      obs?.disconnect();
    };
  }, [target, duration]);
  return <span ref={ref}>{prefix}{val.toLocaleString("fr-FR")}{suffix}</span>;
}

/* ─── Org card — links to /organisations/:id ────────────────────────────── */
function OrgCard({ org }) {
  const initials = org.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <Link
      to={`/organisations/${org.id}`}
      className="group bg-[#FCFAF4] border border-[#E9E1D2] rounded-2xl overflow-hidden hover:shadow-[0_16px_36px_-12px_rgba(13,27,51,0.15)] hover:border-[#DCCFB4] hover:-translate-y-0.5 transition-all duration-200 flex flex-col no-underline"
    >
      {/* Photo banner — shown if org has uploaded one */}
      {org.org_photo_base64 ? (
        <div className="h-44 w-full overflow-hidden shrink-0 bg-stone-100">
          <img
            src={`data:image/jpeg;base64,${org.org_photo_base64}`}
            alt={org.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            style={{ objectPosition: "center 55%" }}
          />
        </div>
      ) : (
        /* Gradient band when no photo */
        <div className="h-1.5 w-full shrink-0"
          style={{ background: "linear-gradient(90deg, #0D1B33, #B8962E)" }} />
      )}

      <div className="p-5 flex flex-col gap-3 flex-1">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0 text-sm font-bold text-white"
            style={{ background: "linear-gradient(135deg, #0D1B33 0%, #1B2A4A 100%)" }}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-stone-900 leading-tight truncate">{org.name}</p>
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3 text-stone-400 shrink-0" />
              <span className="text-xs text-stone-500 truncate">{org.city}</span>
            </div>
          </div>
        </div>
        {/* Tags */}
        {org.tags && org.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {org.tags.slice(0, 3).map(tag => (
              <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ background: "rgba(13,27,51,0.06)", color: "#0D1B33" }}>
                {tag}
              </span>
            ))}
          </div>
        )}
        {/* Description snippet */}
        {org.public_description && (
          <p className="text-xs text-stone-500 line-clamp-2">{org.public_description}</p>
        )}
        {/* CTA */}
        <div className="mt-auto pt-1 flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: "#B8962E" }}>Voir la fiche</span>
          <ChevronRight className="h-3.5 w-3.5" style={{ color: "#B8962E" }} />
        </div>
      </div>
    </Link>
  );
}

/* ─── Campaign card ─────────────────────────────────────────────────────── */
// Premium campaign card — used on the landing page over the navy background.
// Same design language as AllCampaignsPage cards (hero image with gradient
// overlay, theme-coloured CTA, urgency pill).
function CampaignCard({ campaign }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const title = campaign.title || campaign.name || "Campagne";
  const stats = campaign.stats || {};
  const theme = campaign.theme_color || "#0D1B33";
  // Mêmes chiffres que la page campagne et la vignette WhatsApp : le PROMIS,
  // pourcentage du serveur arrondi vers le bas (lib/etatCampagne.js).
  const carte = chiffresCarte(campaign);
  const raised = (campaign.type === "seats_request")
    ? (stats.collected ?? 0)                       // places : comme avant
    : carte.unites ? Math.floor(Number(stats.committed) || 0) : carte.valeur;
  const goal = carte.unites ? 0 : (carte.objectif || 0);
  const pct = carte.pourcentage ?? 0;
  // Type-aware widget: seats campaigns show seats-taken / capacity rather
  // than €, paniers without a target show committed units only, free-amount
  // campaigns keep the classic € thermometer.
  const ctype = campaign.type || "thermometer";
  const seatsTaken = (stats.seats_taken_adults || 0) + (stats.seats_taken_children || 0);
  const seatCapacity = campaign.seat_capacity || 0;
  const unitsCommitted = carte.unites ? carte.valeur : (stats.units_committed || 0);
  const targetUnits = campaign.target_units || 0;
  const orgName = campaign.organization_name;
  const donorsCount = stats.donors_count || 0;
  // image_data_uri (embedded in JSON) is preferred — bypasses the separate
  // /image.png HTTP roundtrip which was failing intermittently behind Vercel.
  const imageUrl = campaign.image_data_uri
    ? campaign.image_data_uri
    : (campaign.image_url
        ? (campaign.image_url.startsWith("http") ? campaign.image_url : `${PUBLIC_API_BASE_URL}${campaign.image_url}`)
        : null);
  const linkTo = campaign.id && /^[0-9a-f-]{20,}$/i.test(campaign.id)
    ? `/c/${campaign.id}`
    : "/#campagnes";

  // Jours restants (pastille d'urgence) : règle du serveur, minuit heure de
  // Paris ; jamais « Dernier jour » sur une campagne terminée.
  const daysLeft = carte.joursRestants;

  return (
    <>
    <Link
      to={linkTo}
      className="group block bg-white border border-stone-200/80 rounded-3xl overflow-hidden hover:shadow-2xl hover:shadow-black/20 hover:-translate-y-1 hover:border-stone-300 transition-all duration-300 no-underline"
    >
      {/* Hero image (or gradient fallback) with gradient overlay.
          object-cover + object-position:center 30% works for landscape
          AND portrait uploads: portraits get their upper third shown
          (where faces / subjects usually sit) instead of dead-centre
          which would crop everything important.
          The small magnifier button (top-right) opens the lightbox so
          donors can see poster-style portraits in full without leaving
          the page. */}
      <div className="relative h-44 overflow-hidden">
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
              style={{ objectPosition: "center 30%" }}
            />
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(180deg, transparent 40%, ${theme}cc 100%)` }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setLightboxOpen(true);
              }}
              className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold text-white bg-black/55 hover:bg-black/75 backdrop-blur-md transition-colors"
              aria-label="Voir l'image en grand"
              title="Voir l'image en grand"
            >
              <ZoomIn className="h-3 w-3" />
              Agrandir
            </button>
          </>
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${theme} 0%, ${theme}dd 50%, #B8962E 100%)` }}
          >
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(255,255,255,0.2) 0%, transparent 50%)",
            }} />
            <div className="relative font-light text-white/40 text-6xl" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>תרומה</div>
          </div>
        )}

        {/* Status pill (top-left) */}
        <div
          className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold text-white backdrop-blur-md"
          style={{ background: "rgba(0,0,0,0.4)" }}
        >
          En cours
        </div>

        {/* Urgency pill (top-right) */}
        {daysLeft !== null && daysLeft <= 14 && (
          <div
            className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold text-white"
            style={{ background: daysLeft <= 3 ? "#DC2626" : "#EA580C" }}
          >
            {daysLeft === 0 ? "Dernier jour" : `J-${daysLeft}`}
          </div>
        )}

        {/* Title + org overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          {orgName && (
            <p className="text-[10px] uppercase tracking-[0.15em] opacity-80 mb-1 truncate">
              {orgName}
            </p>
          )}
          <h3
            className="font-medium text-lg leading-tight line-clamp-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500 }}
          >
            {title}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        <p className="text-xs text-stone-500 mb-4 line-clamp-2 leading-relaxed">
          {campaign.description
            ? campaign.description.replace(/<[^>]+>/g, "").slice(0, 130)
            : "Participez à cette campagne pour soutenir cette synagogue."}
        </p>

        {/* Type-aware progress widget */}
        {ctype === "seats_request" && seatCapacity > 0 ? (
          // Seats: places taken vs capacity, no € jauge.
          <div className="mb-4">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <span className="text-lg font-semibold text-stone-900 tabular-nums">
                  {seatsTaken}
                </span>
                <span className="text-xs text-stone-400 ml-1">
                  / {seatCapacity} place{seatCapacity > 1 ? "s" : ""}
                </span>
              </div>
              <span
                className="text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full"
                style={{ background: `${theme}10`, color: theme }}
              >
                {Math.min(100, Math.round((seatsTaken / seatCapacity) * 100))}%
              </span>
            </div>
            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-1000 ease-out"
                style={{
                  width: `${Math.min(100, Math.round((seatsTaken / seatCapacity) * 100))}%`,
                  background: `linear-gradient(90deg, ${theme}, #B8962E)`,
                }}
              />
            </div>
          </div>
        ) : ctype === "unit_qty" && (targetUnits > 0 || unitsCommitted > 0) ? (
          // Paniers: committed vs target (or just committed if no target).
          <div className="mb-4">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <span className="text-lg font-semibold text-stone-900 tabular-nums">
                  {unitsCommitted}
                </span>
                {targetUnits > 0 && (
                  <span className="text-xs text-stone-400 ml-1">/ {targetUnits}</span>
                )}
                <span className="text-xs text-stone-500 ml-1">
                  {(campaign.unit_label || "unité") + (unitsCommitted > 1 ? "s" : "")}
                </span>
              </div>
              {targetUnits > 0 && (
                <span
                  className="text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full"
                  style={{ background: `${theme}10`, color: theme }}
                >
                  {pct}%
                </span>
              )}
            </div>
            {targetUnits > 0 && (
              <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: `${carte.largeur}%`,
                    background: `linear-gradient(90deg, ${theme}, #B8962E)`,
                  }}
                />
              </div>
            )}
          </div>
        ) : goal > 0 ? (
          // Don libre (thermometer) — classic € progress bar.
          <div className="mb-4">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <span className="text-lg font-semibold text-stone-900 tabular-nums">
                  {Number(raised).toLocaleString("fr-FR")} €
                </span>
                <span className="text-xs text-stone-400 ml-1">/ {Number(goal).toLocaleString("fr-FR")} €</span>
              </div>
              <span
                className="text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full"
                style={{ background: `${theme}10`, color: theme }}
              >
                {pct}%
              </span>
            </div>
            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${carte.largeur}%`, background: `linear-gradient(90deg, ${theme}, #B8962E)` }}
              />
            </div>
          </div>
        ) : raised > 0 && (
          <p className="text-sm font-semibold text-stone-900 mb-4">
            {Number(raised).toLocaleString("fr-FR")} € réunis
          </p>
        )}

        {/* Meta footer */}
        <div className="flex items-center justify-between pt-4 border-t border-stone-100 mb-4 text-xs text-stone-500">
          <span className="inline-flex items-center gap-1.5">
            <div className="h-6 w-6 rounded-full bg-stone-100 flex items-center justify-center">
              <Users className="h-3 w-3 text-stone-500" />
            </div>
            <span className="tabular-nums font-medium">{donorsCount}</span>
            <span>donateur{donorsCount > 1 ? "s" : ""}</span>
          </span>
          {daysLeft !== null && daysLeft > 14 && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>J-{daysLeft}</span>
            </span>
          )}
        </div>

        {/* CTA */}
        <div
          className="flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-semibold text-white transition-all group-hover:gap-2.5"
          style={{ background: theme, boxShadow: `0 4px 14px ${theme}33` }}
        >
          Participer
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
    <CampaignImageLightbox
      src={imageUrl}
      alt={title}
      open={lightboxOpen}
      onClose={() => setLightboxOpen(false)}
    />
    </>
  );
}

// Image URL helper — campaign image_url is returned as a relative path
// ("/api/campaigns/.../image.png"), so we prefix the backend origin.
const PUBLIC_API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/* ─── Org Carousel — same UX as CampaignCarousel but lighter (cream bg) ── */
// Mêmes interactions (scroll-snap + arrows + dots) que le carrousel campagnes,
// adapté pour des cartes orgs sur fond clair. On reuse les classes CSS du
// bloc `<style>` plus bas (.campaign-track) en les renommant côté DOM.
function OrgCarousel({ orgs }) {
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = React.useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
    const slideEl = el.querySelector(".org-slide");
    if (slideEl) {
      const slideW = slideEl.getBoundingClientRect().width + 16;
      setActiveIndex(Math.round(scrollLeft / slideW));
    }
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollBy = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const slideEl = el.querySelector(".org-slide");
    const slideW = slideEl ? slideEl.getBoundingClientRect().width + 16 : 280;
    el.scrollBy({ left: direction * slideW, behavior: "smooth" });
  };

  const scrollToIndex = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const slideEl = el.querySelector(".org-slide");
    if (!slideEl) return;
    const slideW = slideEl.getBoundingClientRect().width + 16;
    el.scrollTo({ left: i * slideW, behavior: "smooth" });
  };

  return (
    <div className="relative">
      <div
        ref={trackRef}
        className="org-track flex gap-4 overflow-x-auto pb-3 -mx-6 px-6 sm:mx-0 sm:px-0"
        style={{ scrollBehavior: "smooth", scrollSnapType: "x mandatory" }}
      >
        {orgs.map((o, idx) => (
          <div
            key={o.id}
            className="org-slide w-[85vw] sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] xl:w-[calc(25%-12px)]"
            style={{
              scrollSnapAlign: "start",
              flexShrink: 0,
              animation: `landingCardFade 0.5s ${Math.min(idx, 5) * 60}ms both ease-out`,
            }}
          >
            <OrgCard org={o} />
          </div>
        ))}
      </div>

      {/* Arrows (desktop only) */}
      {orgs.length > 3 && (
        <>
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            disabled={!canScrollLeft}
            aria-label="Précédent"
            className="hidden sm:flex absolute -left-2 top-[40%] -translate-y-1/2 h-11 w-11 items-center justify-center rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 z-10"
            style={{ background: "white", border: "1px solid #E5E7EB", boxShadow: "0 4px 12px rgba(13,27,51,0.08)" }}
          >
            <ChevronRight className="h-4 w-4 text-stone-700 rotate-180" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            disabled={!canScrollRight}
            aria-label="Suivant"
            className="hidden sm:flex absolute -right-2 top-[40%] -translate-y-1/2 h-11 w-11 items-center justify-center rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 z-10"
            style={{ background: "white", border: "1px solid #E5E7EB", boxShadow: "0 4px 12px rgba(13,27,51,0.08)" }}
          >
            <ChevronRight className="h-4 w-4 text-stone-700" />
          </button>
        </>
      )}

      {/* Dots */}
      {orgs.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          {orgs.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollToIndex(i)}
              aria-label={`Aller à l'organisation ${i + 1}`}
              className="transition-all rounded-full"
              style={{
                width: i === activeIndex ? "20px" : "6px",
                height: "6px",
                background: i === activeIndex ? "#B8962E" : "rgba(13,27,51,0.15)",
              }}
            />
          ))}
        </div>
      )}

      {/* CSS for org-track scrollbar hiding */}
      <style>{`
        .org-track::-webkit-scrollbar { display: none; }
        .org-track { scrollbar-width: none; -webkit-overflow-scrolling: touch; }
      `}</style>
    </div>
  );
}

/* ─── Campaign Carousel — horizontal scroll-snap with arrows & dots ──── */
function CampaignCarousel({ campaigns }) {
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Update scroll buttons + active dot based on scroll position
  const updateScrollState = React.useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
    // Compute slide width to determine active index (1 slide ≈ clientWidth on mobile, 1/2 on tablet, 1/3 on desktop)
    const slideEl = el.querySelector(".campaign-slide");
    if (slideEl) {
      const slideW = slideEl.getBoundingClientRect().width + 20; /* gap */
      setActiveIndex(Math.round(scrollLeft / slideW));
    }
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollBy = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const slideEl = el.querySelector(".campaign-slide");
    const slideW = slideEl ? slideEl.getBoundingClientRect().width + 20 : 320;
    el.scrollBy({ left: direction * slideW, behavior: "smooth" });
  };

  const scrollToIndex = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const slideEl = el.querySelector(".campaign-slide");
    if (!slideEl) return;
    const slideW = slideEl.getBoundingClientRect().width + 20;
    el.scrollTo({ left: i * slideW, behavior: "smooth" });
  };

  return (
    <div className="relative">
      {/* Track — horizontal scroll with snap */}
      <div
        ref={trackRef}
        className="campaign-track flex gap-5 overflow-x-auto pb-3 -mx-6 px-6 sm:mx-0 sm:px-0"
        style={{ scrollBehavior: "smooth" }}
      >
        {campaigns.map((c, idx) => (
          <div
            key={c.id}
            className="campaign-slide w-[85vw] sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]"
            style={{ animation: `landingCardFade 0.5s ${Math.min(idx, 5) * 60}ms both ease-out` }}
          >
            <CampaignCard campaign={c} />
          </div>
        ))}
      </div>

      {/* Navigation arrows (hidden on mobile, visible >= sm) */}
      {campaigns.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            disabled={!canScrollLeft}
            aria-label="Précédent"
            className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 h-12 w-12 items-center justify-center rounded-full backdrop-blur-md transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 z-10"
            style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(184,150,46,0.3)" }}
          >
            <ChevronRight className="h-5 w-5 text-white rotate-180" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            disabled={!canScrollRight}
            aria-label="Suivant"
            className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 h-12 w-12 items-center justify-center rounded-full backdrop-blur-md transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 z-10"
            style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(184,150,46,0.3)" }}
          >
            <ChevronRight className="h-5 w-5 text-white" />
          </button>
        </>
      )}

      {/* Dots indicator */}
      {campaigns.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          {campaigns.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollToIndex(i)}
              aria-label={`Aller à la campagne ${i + 1}`}
              className="transition-all rounded-full"
              style={{
                width: i === activeIndex ? "24px" : "8px",
                height: "8px",
                background: i === activeIndex ? "#D4B45C" : "rgba(255,255,255,0.25)",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Step ──────────────────────────────────────────────────────────────── */
function Step({ n, title, desc }) {
  return (
    <div className="flex gap-5 items-start">
      <div className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
        style={{ background: "linear-gradient(135deg, #0D1B33 0%, #B8962E 100%)" }}>
        {n}
      </div>
      <div>
        <p className="font-semibold text-stone-900 mb-1">{title}</p>
        <p className="text-sm text-stone-500 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({ organizations: 0, donors: 0, total_collected: 0, campaigns: 0 });
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [donateModal, setDonateModal] = useState(null); // selected org
  // Nav transparente tant qu'on est SUR le hero vidéo, solide une fois passé.
  const [overHero, setOverHero] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false); // menu mobile (hamburger)
  const [pricingTiers, setPricingTiers] = useState([]); // grille publique des plans

  useEffect(() => {
    apiClient.get("/subscription/tiers")
      .then(res => setPricingTiers(res.data?.tiers || []))
      .catch(() => setPricingTiers([]));
  }, []);

  // Arrivée directe sur terouma.fr/#tarifs : la section n'existe qu'après le
  // fetch des tiers (et le mount force scrollTo(0,0)) → on re-saute vers
  // l'ancre une fois la grille rendue.
  useEffect(() => {
    if (pricingTiers.length > 0 && window.location.hash === "#tarifs") {
      requestAnimationFrame(() => {
        document.getElementById("tarifs")?.scrollIntoView({ behavior: "auto", block: "start" });
      });
    }
  }, [pricingTiers]);

  // Toujours démarrer EN HAUT au chargement (sinon le navigateur restaure la
  // position de scroll → on retombe à la fin de l'animation du hero).
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    return () => {
      if ("scrollRestoration" in window.history) window.history.scrollRestoration = "auto";
    };
  }, []);

  // Pack « léger » : apparitions au scroll (sections) + smooth scroll velours.
  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const els = Array.from(document.querySelectorAll(".reveal"));
    let obs;
    if (reduce) {
      els.forEach(el => el.classList.add("in"));
    } else {
      obs = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("in"); obs.unobserve(e.target); } });
      }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
      els.forEach(el => obs.observe(el));
    }
    // Smooth scroll « velours » à la molette (desktop). Le tactile garde son
    // inertie native → le hero mobile (scrubé) n'est pas modifié.
    let lenis, raf;
    if (!reduce) {
      import("lenis").then(({ default: Lenis }) => {
        lenis = new Lenis({ duration: 0.9, smoothWheel: true });
        const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
        raf = requestAnimationFrame(loop);
      }).catch(() => {});
    }
    return () => {
      if (obs) obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async (isInitial) => {
      try {
        // Cache buster ⇒ force fresh fetch even si Vercel CDN ou browser cache
        const cacheBust = `?t=${Date.now()}`;
        const [orgsRes, statsRes, campaignsRes] = await Promise.all([
          apiClient.get("/organizations/public/directory").catch(() => ({ data: { organizations: [] } })),
          apiClient.get(`/organizations/public/stats${cacheBust}`).catch(() => ({ data: {} })),
          apiClient.get(`/campaigns/public${cacheBust}`).catch(() => ({ data: { campaigns: [] } })),
        ]);
        if (cancelled) return;
        setOrgs(orgsRes.data.organizations || []);
        setStats(statsRes.data || {});
        setCampaigns(campaignsRes.data?.campaigns || []);
      } finally {
        if (!cancelled && isInitial) setLoadingOrgs(false);
      }
    };

    // Chargement initial
    fetchAll(true);

    // Refresh périodique des compteurs (toutes les 45 s) — la landing
    // reste ouverte longtemps sur certains écrans (TV de hall, etc.),
    // les visiteurs doivent voir les chiffres bouger en quasi temps réel.
    const interval = setInterval(() => fetchAll(false), 45000);
    // Rafraîchit aussi quand l'onglet revient au premier plan
    const onFocus = () => fetchAll(false);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Barre du haut : transparente tant que le hero (vidéo) est derrière elle,
  // solide dès qu'on atteint le contenu clair en dessous.
  useEffect(() => {
    const NAV_H = 80;
    const onScroll = () => {
      const hero = document.querySelector("[data-hero-scrub]");
      setOverHero(hero ? hero.getBoundingClientRect().bottom > NAV_H : false);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const filtered = orgs.filter(o =>
    !search || o.name.toLowerCase().includes(search.toLowerCase()) || o.city.toLowerCase().includes(search.toLowerCase())
  );

  // Show real orgs if available, otherwise show empty state (no fake data)
  const displayOrgs = filtered;
  const isLaunchMode = orgs.length === 0 && !loadingOrgs && !search;
  // Toujours afficher CE QUE L'API RENVOIE (zero compris). L'ancien code
  // basculait sur des zéros si stats.organizations était falsy — ce qui
  // arrivait quand /stats répondait avec un objet vide pendant le chargement,
  // et figeait l'affichage à 0/0/0/0 même après que la réponse arrive.
  const displayStats = {
    organizations: stats.organizations ?? 0,
    donors: stats.donors ?? 0,
    total_collected: stats.total_collected ?? 0,
    campaigns: stats.campaigns ?? 0,
  };

  // ── ⏳ VITRINE DE LANCEMENT — TEMPORAIRE, à retirer en Hechvan 5787 ──
  // Le compteur « Collecté » affiche un montant FIXE tant que la plateforme
  // n'a qu'une poignée de synagogues (demande Avi, 31/07/2026) : le vrai
  // total ne fait pas encore crédible en page d'accueil.
  // La mécanique réelle est CONSERVÉE (displayStats ci-dessus reçoit
  // toujours la vraie valeur de l'API) : pour revenir aux vraies données,
  // vider LAUNCH_OVERRIDES ({}) — une seule ligne.
  // 📅 Rappel programmé le 15 octobre 2026 (Hechvan) pour faire ce retour.
  const LAUNCH_OVERRIDES = { total_collected: 304000 };
  const shownStats = { ...displayStats, ...LAUNCH_OVERRIDES };

  // Transitions entre bandes : plus de fondu dégradé (jugé « cassé »). On passe
  // à un langage « panneau qui lévite » — chaque bande chevauche la précédente
  // avec un coin arrondi + un liseré d'or, comme des cartes empilées. Voir les
  // sections #organisations / #campagnes / Kiddoush / footer ci-dessous.

  return (
    <div className="min-h-screen font-preview" style={{ background: "#F7F4EC", fontFamily: "'Outfit', sans-serif" }}>

      {/* ── Navbar — FIXED (le hero passe dessous, même tout en haut) :
            transparente sur le hero, solide au scroll ────────────────────── */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${overHero ? "bg-transparent" : "bg-white/90 backdrop-blur border-b border-stone-200"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center justify-between gap-3">
          {/* Logo : sur le hero (barre transparente) on l'enferme dans une pastille
              blanche pour qu'il ressorte ; sur fond clair (barre solide) il est
              en navy sans pastille. */}
          {/* Pastille UNIQUEMENT autour de l'icône (la goutte) via onDark ; le
              wordmark reste en blanc sur le hero, en navy sur fond clair. */}
          <div className="shrink-0">
            <div className="md:hidden">
              <TeroumaLogo size={34} variant="full" onDark={overHero} textColor={overHero ? "#FFFFFF" : "#0D1B33"} />
            </div>
            <div className="hidden md:block">
              <TeroumaLogo size={44} variant="full" onDark={overHero} textColor={overHero ? "#FFFFFF" : "#0D1B33"} />
            </div>
          </div>
          <div className={`hidden md:flex items-center gap-6 text-sm transition-colors ${overHero ? "text-white/85" : "text-stone-600"}`}>
            <a href="#organisations" className={overHero ? "hover:text-white transition-colors" : "hover:text-stone-900 transition-colors"}>Organisations</a>
            <a href="#campagnes" className={overHero ? "hover:text-white transition-colors" : "hover:text-stone-900 transition-colors"}>Campagnes</a>
            <a href="#comment-ca-marche" className={overHero ? "hover:text-white transition-colors" : "hover:text-stone-900 transition-colors"}>Comment ça marche</a>
            <a href="#tarifs" className={overHero ? "hover:text-white transition-colors" : "hover:text-stone-900 transition-colors"}>Tarifs</a>
            <Link to="/kiddoush" className={overHero ? "hover:text-white transition-colors" : "hover:text-stone-900 transition-colors"}>Kiddoush</Link>
            <Link to="/contact" className={overHero ? "hover:text-white transition-colors" : "hover:text-stone-900 transition-colors"}>Contact</Link>
          </div>
          <div className="flex items-center gap-2">
            {/* Desktop : les deux boutons */}
            <div className="hidden md:flex items-center gap-2">
              <Link to="/mon-espace"
                className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full border-2 transition-colors hover:opacity-90"
                style={{ borderColor: "#B8962E", color: overHero ? "#E7C76A" : "#B8962E", background: "transparent" }}>
                Espace donateur
              </Link>
              <Link to="/login"
                className="text-sm font-medium px-4 py-2 rounded-full transition-colors hover:opacity-90"
                style={overHero ? { background: "#FFFFFF", color: "#0D1B33" } : { background: "#0D1B33", color: "#FFFFFF" }}>
                Connexion gestion
              </Link>
            </div>
            {/* Mobile : menu hamburger (contient liens + les deux boutons) */}
            <button type="button" aria-label="Menu"
              onClick={() => setMenuOpen(o => !o)}
              className="md:hidden h-10 w-10 -mr-1 inline-flex items-center justify-center"
              style={{ color: overHero ? "#FFFFFF" : "#0D1B33" }}>
              {menuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>

        {/* Panneau mobile déroulant */}
        {menuOpen && (
          <div className="md:hidden border-t border-white/10 shadow-2xl" style={{ background: "#0D1B33" }}>
            <div className="px-5 py-2 flex flex-col">
              {[
                { label: "Organisations", href: "#organisations" },
                { label: "Campagnes", href: "#campagnes" },
                { label: "Comment ça marche", href: "#comment-ca-marche" },
                { label: "Tarifs", href: "#tarifs" },
              ].map(l => (
                <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}
                  className="py-3 text-[15px] text-white/90 border-b border-white/10">{l.label}</a>
              ))}
              <Link to="/kiddoush" onClick={() => setMenuOpen(false)} className="py-3 text-[15px] text-white/90 border-b border-white/10">Kiddoush</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="py-3 text-[15px] text-white/90 border-b border-white/10">Contact</Link>
              <div className="flex gap-2 pt-4 pb-2">
                <Link to="/mon-espace" onClick={() => setMenuOpen(false)}
                  className="flex-1 text-center text-sm font-medium py-2.5 rounded-full border-2"
                  style={{ borderColor: "#B8962E", color: "#E7C76A" }}>
                  Espace donateur
                </Link>
                <Link to="/login" onClick={() => setMenuOpen(false)}
                  className="flex-1 text-center text-sm font-medium py-2.5 rounded-full"
                  style={{ background: "#FFFFFF", color: "#0D1B33" }}>
                  Connexion gestion
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* ── Hero cinématique ÉPINGLÉ — la séquence d'images défile au scroll et
            « bloque » la page tant qu'elle n'est pas terminée. Épuré : titre,
            sous-titre, 1 action, statistiques. ──────────────────────────────── */}
      {/* Hauteur = durée du « pin » : plus c'est court, plus la vidéo défile vite
          et moins on reste bloqué avant de pouvoir scroller. Mobile raccourci en
          priorité (retours : « relou d'être bloqué sur tél »). */}
      <section data-hero-scrub className="relative h-[165vh] md:h-[220vh]" style={{ background: "#0D1B33" }}>
        <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col">
          {/* Fond : séquence d'images synchronisée au scroll, sur TOUS les appareils
              (mobile = jeu d'images allégé). Image déjà décodée → scrub fluide. */}
          <HeroScrollFrames
            count={84}
            mobileCount={40}
            dir="/hero-frames"
            mobileDir="/hero-frames-m"
            className="absolute inset-0 h-full w-full object-cover z-0"
          />
          {/* Voile : assombrit le haut/bas pour la lisibilité, laisse le doré au centre */}
          <div className="absolute inset-0 z-0 pointer-events-none"
            style={{ background: "linear-gradient(180deg, rgba(13,27,51,0.78) 0%, rgba(13,27,51,0.22) 32%, rgba(13,27,51,0.22) 58%, rgba(13,27,51,0.88) 100%)" }} />
          {/* Filet doré en haut */}
          <div className="absolute top-0 left-0 right-0 h-px z-10" style={{ background: "linear-gradient(90deg, transparent, rgba(184,150,46,0.7) 50%, transparent)" }} />

          {/* Contenu minimaliste, centré */}
          <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6">
            <h1 className="hero-gilt font-light tracking-tight leading-[1.05] text-5xl sm:text-6xl lg:text-7xl"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Soutenez votre communauté
            </h1>
            <p className="mt-6 text-white/90 text-base sm:text-lg max-w-xl"
              style={{ textShadow: "0 2px 14px rgba(0,0,0,.85)" }}>
              La plateforme de dons des synagogues, partout en France.
            </p>
            {/* Indice de scroll (remplace le bouton « Trouver ma synagogue » qui
                faisait un saut et zappait l'animation). Un clic fait défiler en
                douceur d'un écran → l'animation se JOUE au lieu d'être sautée. */}
            <button
              type="button"
              onClick={() => window.scrollTo({ top: window.innerHeight, behavior: "smooth" })}
              aria-label="Faites défiler pour trouver votre synagogue"
              className="mt-10 inline-flex flex-col items-center gap-2.5 group"
            >
              <span className="text-[11px] sm:text-xs uppercase tracking-[0.28em] text-white/75 group-hover:text-white transition-colors whitespace-nowrap"
                style={{ textShadow: "0 2px 12px rgba(0,0,0,.85)" }}>
                Faites défiler
              </span>
              <span className="h-9 w-9 rounded-full flex items-center justify-center animate-bounce"
                style={{ border: "1px solid rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.06)", boxShadow: "0 4px 16px rgba(0,0,0,0.35)" }}>
                <ChevronDown className="h-5 w-5 text-white/90" />
              </span>
            </button>
          </div>

          {/* Statistiques — minimalistes, en bas */}
          <div className="relative z-10 pb-10 px-6">
            <div className="max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5">
              {[
                { label: "Organisations", value: shownStats.organizations, suffix: "" },
                { label: "Donateurs", value: shownStats.donors, suffix: shownStats.donors > 0 ? "+" : "" },
                shownStats.total_collected >= 1000
                  ? { label: "Collecté", value: Math.round(shownStats.total_collected / 1000), suffix: " k€" }
                  : { label: "Collecté", value: Math.round(shownStats.total_collected), suffix: " €" },
                { label: "Campagnes", value: shownStats.campaigns, suffix: "" },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <p className="text-2xl sm:text-3xl font-semibold text-white">
                    <Counter target={s.value} suffix={s.suffix} />
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.18em] mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Organisations — PANNEAU qui « LÉVITE » par-dessus la fin de la vidéo
            (remplace l'ancien fondu navy→crème). Le clair se soulève sur le
            navy immersif : coin arrondi + liseré d'or + ombre portée. Le
            chevauchement (-mt) ne mord que sur le navy APRÈS la fin du scrub
            vidéo (option A), jamais pendant l'animation. ───────────────────── */}
      <section id="organisations"
        className="relative z-10 px-6 pt-16 md:pt-20 pb-20 -mt-[16vh] md:-mt-[20vh] rounded-t-[32px] md:rounded-t-[48px]"
        style={{ background: "#F7F4EC", borderTop: "1.5px solid rgba(184,150,46,0.55)", boxShadow: "0 -50px 90px -40px rgba(0,0,0,0.55)" }}>
        <div className="max-w-7xl mx-auto">
          {/* En-tête (sans search bar — déléguée à /organisations) */}
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "#B8962E" }}>Réseau national</p>
            <h2 className="text-3xl font-light text-stone-900 tracking-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Nos organisations partenaires
            </h2>
            <p className="text-stone-500 mt-2 text-sm">Synagogues et associations juives à travers la France</p>
          </div>

          {loadingOrgs ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white border border-stone-200 rounded-2xl p-5 animate-pulse">
                  <div className="flex gap-3 mb-3">
                    <div className="h-11 w-11 rounded-xl bg-stone-100" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-stone-100 rounded w-3/4" />
                      <div className="h-2.5 bg-stone-100 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="h-8 bg-stone-100 rounded-xl mt-4" />
                </div>
              ))}
            </div>
          ) : isLaunchMode ? (
            /* Programme pilote — no fake orgs */
            <div className="rounded-3xl overflow-hidden border-2 border-dashed border-stone-200 bg-white">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-10 flex flex-col justify-center">
                  <p className="text-[11px] uppercase mb-5 w-fit"
                    style={{ color: "#B8962E", letterSpacing: "0.28em", fontWeight: 500 }}>
                    Programme pilote · 2026
                  </p>
                  <h3 className="text-2xl font-semibold text-stone-900 mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Votre organisation sera parmi les premières
                  </h3>
                  <p className="text-stone-500 text-sm leading-relaxed mb-6">
                    Terouma lance son réseau d'organisations partenaires en France. Rejoignez le programme pilote et bénéficiez d'un accès prioritaire et d'un accompagnement personnalisé.
                  </p>
                  <div className="space-y-2 text-sm text-stone-600 mb-8">
                    {["Accompagnement à la mise en place offert", "Accompagnement dédié au démarrage", "Votre nom mis en avant sur la plateforme", "Un plan gratuit pour découvrir, sans engagement"].map(f => (
                      <div key={f} className="flex items-center gap-2">
                        <div className="h-4 w-4 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(184,150,46,0.15)" }}>
                          <span style={{ color: "#B8962E", fontSize: 10 }}>✓</span>
                        </div>
                        {f}
                      </div>
                    ))}
                  </div>
                  <Link to="/contact?type=org"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-medium w-fit transition-colors"
                    style={{ background: "#0D1B33" }}
                    onMouseEnter={e => e.currentTarget.style.background="#1B2A4A"}
                    onMouseLeave={e => e.currentTarget.style.background="#0D1B33"}>
                    Rejoindre le programme pilote
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="p-10 flex flex-col justify-center"
                  style={{ background: "rgba(13,27,51,0.03)", borderLeft: "1px dashed #E5E7EB" }}>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">Ce que vous obtenez</p>
                  <div className="space-y-4">
                    {[
                      { icon: HandCoins, label: "Gestion des dons", desc: "Engagements, paiements, relances automatiques" },
                      { icon: FileText, label: "Reçus Cerfa automatiques", desc: "PDF officiel 11580*03 généré instantanément" },
                      { icon: Users, label: "Magic Links donateurs", desc: "Lien de paiement personnel par email ou SMS" },
                      { icon: Calendar, label: "Kiddoush Planner", desc: "Réservations en ligne avec calendrier" },
                    ].map(f => {
                      const Icon = f.icon;
                      return (
                        <div key={f.label} className="flex items-start gap-3">
                          <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                            style={{ background: "rgba(13,27,51,0.06)" }}>
                            <Icon className="h-4 w-4 text-stone-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-stone-900">{f.label}</p>
                            <p className="text-xs text-stone-500">{f.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : search && displayOrgs.length === 0 ? (
            <div className="text-center py-16 text-stone-400">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-stone-200" />
              <p>Aucune organisation trouvée pour "{search}"</p>
              <Link
                to="/organisations"
                className="inline-flex items-center gap-1.5 text-sm mt-4"
                style={{ color: "#B8962E" }}
              >
                Voir l'annuaire complet <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ) : displayOrgs.length === 0 ? (
            <div className="text-center py-16 text-stone-400">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-stone-200" />
              <p>Aucune organisation pour le moment</p>
            </div>
          ) : (
            <>
              {/* Carrousel orgs — top 12 pour rester rapide */}
              <OrgCarousel orgs={displayOrgs.slice(0, 12)} />

              {/* CTA "Voir toutes les organisations" — mène vers /organisations */}
              <div className="text-center mt-10">
                <Link
                  to="/organisations"
                  className="inline-flex items-center gap-2 text-sm font-semibold px-7 py-3.5 rounded-full transition-all hover:scale-[1.03]"
                  style={{
                    background: "#0D1B33",
                    color: "white",
                    boxShadow: "0 8px 24px rgba(13,27,51,0.20)",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#1B2A4A"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#0D1B33"; }}
                >
                  Voir toutes les organisations
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="text-stone-400 text-xs mt-3">
                  {orgs.length} organisation{orgs.length > 1 ? "s" : ""} · recherche, ville, tags
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── Campagnes — PANNEAU SOMBRE qui lévite par-dessus le crème (remplace
            le fondu crème→navy). Même langage : arrondi + liseré d'or + ombre. */}
      {true && (
        <section id="campagnes"
          className="relative z-20 pt-16 pb-24 px-6 overflow-hidden rounded-t-[32px] md:rounded-t-[48px] -mt-8 md:-mt-12"
          style={{ background: "#0D1B33", borderTop: "1.5px solid rgba(184,150,46,0.55)", boxShadow: "0 -40px 80px -36px rgba(13,27,51,0.5)" }}>
          {/* Décoration: orbes radiales gold en arrière-plan */}
          <div
            className="absolute -top-40 -left-32 h-[28rem] w-[28rem] rounded-full opacity-20 blur-3xl pointer-events-none"
            style={{ background: "radial-gradient(circle, #B8962E 0%, transparent 70%)" }}
          />
          <div
            className="absolute -bottom-32 -right-32 h-[32rem] w-[32rem] rounded-full opacity-15 blur-3xl pointer-events-none"
            style={{ background: "radial-gradient(circle, #B8962E 0%, transparent 70%)" }}
          />

          <div className="relative max-w-7xl mx-auto">
            {/* En-tête */}
            <div className="text-center mb-12">
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.2em] mb-4 border"
                style={{ background: "rgba(184,150,46,0.12)", color: "#D4B45C", borderColor: "rgba(184,150,46,0.25)" }}
              >
                <Megaphone className="h-3 w-3" /> Collectes actives
              </div>
              <h2
                className="text-4xl sm:text-5xl font-light text-white tracking-tight"
                style={{ fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1.1 }}
              >
                Soutenez <em className="not-italic" style={{ color: "#D4B45C" }}>une cause</em><br />
                qui vous touche
              </h2>
              <p className="text-stone-400 mt-4 text-base max-w-xl mx-auto">
                Découvrez les campagnes actives des synagogues partenaires.
                Chaque don ouvre droit à un reçu Cerfa et 66 % de réduction d'impôt.
              </p>
            </div>

            {/* Loading skeletons */}
            {loadingOrgs && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden animate-pulse">
                    <div className="h-44 bg-white/5" />
                    <div className="p-5 space-y-3">
                      <div className="h-3 bg-white/10 rounded w-3/4" />
                      <div className="h-3 bg-white/10 rounded w-1/2" />
                      <div className="h-2 bg-white/10 rounded w-full mt-4" />
                      <div className="h-10 bg-white/10 rounded-2xl mt-4" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Carrousel campaigns */}
            {!loadingOrgs && campaigns && campaigns.length > 0 && (
              <CampaignCarousel campaigns={campaigns} />
            )}

            {/* EMPTY STATE — aucune campagne active */}
            {!loadingOrgs && campaigns && campaigns.length === 0 && (
              <div className="text-center py-12 max-w-2xl mx-auto">
                <div
                  className="h-20 w-20 rounded-3xl flex items-center justify-center mx-auto mb-6 border"
                  style={{ background: "rgba(184,150,46,0.08)", borderColor: "rgba(184,150,46,0.25)" }}
                >
                  <Megaphone className="h-9 w-9" style={{ color: "#D4B45C" }} />
                </div>
                <h3
                  className="text-2xl text-white mb-3"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  Bientôt, retrouvez ici nos collectes
                </h3>
                <p className="text-stone-400 text-sm leading-relaxed mb-8 max-w-md mx-auto">
                  Les synagogues partenaires préparent leurs prochaines campagnes.
                  Revenez bientôt pour découvrir comment soutenir une cause qui vous touche.
                </p>
                <Link
                  to="/campagnes"
                  className="inline-flex items-center gap-2 text-sm font-semibold px-7 py-3.5 rounded-full transition-all hover:scale-[1.03]"
                  style={{ background: "#B8962E", color: "white", boxShadow: "0 8px 24px rgba(184,150,46,0.3)" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#D4AE4A"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#B8962E"; }}
                >
                  Explorer la page des campagnes
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            {/* CTA central "Voir toutes les campagnes" — quand il y a des campagnes */}
            {!loadingOrgs && campaigns && campaigns.length > 0 && (
              <div className="text-center mt-12">
                <Link
                  to="/campagnes"
                  className="inline-flex items-center gap-2 text-base font-semibold px-8 py-4 rounded-full transition-all hover:scale-[1.03]"
                  style={{ background: "#B8962E", color: "white", boxShadow: "0 12px 32px rgba(184,150,46,0.35)" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#D4AE4A"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#B8962E"; }}
                >
                  Voir toutes les campagnes
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="text-stone-400 text-xs mt-3">
                  {campaigns.length} campagne{campaigns.length > 1 ? "s" : ""} en cours · recherche, filtres, tri
                </p>
              </div>
            )}
          </div>

          {/* Animation + Carrousel CSS */}
          <style>{`
            @keyframes landingCardFade {
              from { opacity: 0; transform: translateY(20px); }
              to   { opacity: 1; transform: translateY(0);    }
            }
            .campaign-track {
              scroll-snap-type: x mandatory;
              -webkit-overflow-scrolling: touch;
              scrollbar-width: none;
            }
            .campaign-track::-webkit-scrollbar { display: none; }
            .campaign-slide {
              scroll-snap-align: start;
              flex-shrink: 0;
            }
          `}</style>
        </section>
      )}

      {/* ── Impact fiscal (slider) ───────────────────────────────────── */}
      <TaxSlider />

      {/* ── Kiddoush + Espace donateur — PANNEAU CLAIR qui lévite par-dessus le
            navy (remplace le fondu navy→crème). Démarre la bande claire. ──── */}
      <section
        className="relative z-30 px-6 pt-16 md:pt-20 pb-16 -mt-8 md:-mt-12 rounded-t-[32px] md:rounded-t-[48px] reveal"
        style={{ background: "#F7F4EC", borderTop: "1.5px solid rgba(184,150,46,0.55)", boxShadow: "0 -36px 70px -44px rgba(13,27,51,0.35)" }}>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6">
          {/* Kiddoush */}
          <div className="rounded-2xl p-8 border-2 border-[#E9E1D2] bg-[#FCFAF4] hover:shadow-[0_16px_36px_-12px_rgba(13,27,51,0.15)] hover:border-[#DCCFB4] transition-all">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(184,150,46,0.1)" }}>
              <Calendar className="h-6 w-6" style={{ color: "#B8962E" }} />
            </div>
            <h3 className="text-xl font-semibold text-stone-900 mb-2">Réserver un Kiddouch</h3>
            <p className="text-stone-500 text-sm mb-5 leading-relaxed">Offrez un Kiddouch à votre communauté pour un Chabbat, une Bar Mitsvah ou toute autre occasion. Réservation en ligne, confirmation par l'organisation.</p>
            <Link to="/kiddoush" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-medium transition-colors" style={{ background: "#0D1B33" }}
              onMouseEnter={e => e.currentTarget.style.background="#1B2A4A"} onMouseLeave={e => e.currentTarget.style.background="#0D1B33"}>
              Réserver un Kiddouch <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          {/* Espace donateur */}
          <div className="rounded-2xl p-8 border-2 border-[#E9E1D2] bg-[#FCFAF4] hover:shadow-[0_16px_36px_-12px_rgba(13,27,51,0.15)] hover:border-[#DCCFB4] transition-all">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(13,27,51,0.06)" }}>
              <FileText className="h-6 w-6 text-stone-700" />
            </div>
            <h3 className="text-xl font-semibold text-stone-900 mb-2">Mon espace donateur</h3>
            <p className="text-stone-500 text-sm mb-5 leading-relaxed">Retrouvez l'historique de vos dons et téléchargez vos reçus fiscaux Cerfa. Entrez simplement votre email — aucun mot de passe.</p>
            <Link to="/mon-espace" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 text-stone-700 text-sm font-medium transition-colors border-stone-300 hover:bg-stone-50">
              Accéder à mon espace <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────── */}
      <section id="comment-ca-marche" className="py-20 px-6 reveal">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "#B8962E" }}>Simple & rapide</p>
              <h2 className="text-3xl font-light text-stone-900 tracking-tight mb-4"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Faire un don en 3 étapes
              </h2>
              <p className="text-stone-500 text-sm mb-10">Aucune inscription requise pour les donateurs. En 2 minutes, votre don est enregistré et un reçu fiscal vous est envoyé.</p>
              <div className="space-y-6">
                <Step n="1" title="Trouvez votre organisation" desc="Cherchez votre synagogue par ville ou nom dans notre annuaire national." />
                <Step n="2" title="Choisissez votre engagement" desc="Sélectionnez un montant libre ou parmi les motifs proposés (Kiddouch, Aliya Torah, don libre…)." />
                <Step n="3" title="Recevez votre reçu fiscal" desc="Un reçu Cerfa 11580*03 officiel vous est envoyé automatiquement par email pour votre déclaration." />
              </div>
            </div>

            {/* Feature grid */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Shield, title: "Paiement sécurisé", desc: "Stripe · chiffrement bout-en-bout", accent: false },
                { icon: FileText, title: "Reçu Cerfa automatique", desc: "Réduction d'impôt de 66%", accent: true },
                { icon: Zap, title: "En 2 minutes", desc: "Sans inscription pour le donateur", accent: false },
                { icon: Lock, title: "Lien personnel", desc: "Magic Link sécurisé et unique", accent: false },
                { icon: Globe, title: "47 organisations", desc: "À travers toute la France", accent: true },
                { icon: TrendingUp, title: "Suivi en temps réel", desc: "Tableau de bord pour les gestionnaires", accent: false },
              ].map(f => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="bg-[#FCFAF4] border border-[#E9E1D2] rounded-2xl p-4 hover:shadow-[0_12px_28px_-12px_rgba(13,27,51,0.14)] hover:border-[#DCCFB4] transition-all">
                    <div className="h-9 w-9 rounded-xl flex items-center justify-center mb-3"
                      style={{ background: f.accent ? "rgba(184,150,46,0.1)" : "rgba(13,27,51,0.05)" }}>
                      <Icon className="h-4 w-4" style={{ color: f.accent ? "#B8962E" : "#0D1B33" }} />
                    </div>
                    <p className="text-sm font-semibold text-stone-900 mb-0.5">{f.title}</p>
                    <p className="text-xs text-stone-500 leading-relaxed">{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── For organizations CTA ──────────────────────────────────────── */}
      <section className="py-16 px-6 reveal">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-3xl overflow-hidden relative"
            style={{ background: "linear-gradient(135deg, #0D1B33 0%, #1B2A4A 60%, #0D1B33 100%)" }}>
            <div className="absolute inset-0 opacity-10"
              style={{ backgroundImage: "radial-gradient(circle at 70% 50%, #B8962E 0%, transparent 60%)" }} />
            <div className="relative px-8 py-12 md:px-14 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "#B8962E" }}>Pour les organisations</p>
                <h2 className="text-3xl font-light text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Votre synagogue veut<br />rejoindre Terouma ?
                </h2>
                <p className="text-stone-300 text-sm leading-relaxed mb-6">
                  Gérez vos dons, relancez automatiquement vos donateurs, générez des reçus Cerfa et suivez vos campagnes. Simple, rapide, conforme.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/contact?type=org"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium text-white transition-colors"
                    style={{ background: "#B8962E" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#a07828"}
                    onMouseLeave={e => e.currentTarget.style.background = "#B8962E"}>
                    Rejoindre le programme
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium border transition-colors"
                    style={{ borderColor: "rgba(255,255,255,0.25)", color: "white" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <Mail className="h-4 w-4" />
                    Nous contacter
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Users, label: "Gestion donateurs", desc: "Fiches complètes" },
                  { icon: HandCoins, label: "Suivi des dons", desc: "Engagements & paiements" },
                  { icon: FileText, label: "Cerfa automatique", desc: "PDF officiel" },
                  { icon: Mail, label: "Magic Links", desc: "Paiement sans friction" },
                ].map(f => {
                  const Icon = f.icon;
                  return (
                    <div key={f.label} className="rounded-xl p-4"
                      style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)" }}>
                      <Icon className="h-5 w-5 mb-2" style={{ color: "#B8962E" }} />
                      <p className="text-sm font-medium text-white">{f.label}</p>
                      <p className="text-xs text-stone-400 mt-0.5">{f.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Tarifs — grille publique des plans (lus depuis l'API, même source
            que la page « Mon abonnement » des gestionnaires).
            ⚠️ PAS de classe `reveal` ici : la section est rendue APRÈS le
            fetch des tiers, or l'IntersectionObserver du reveal ne scanne
            qu'au montage de la page → elle resterait à opacity 0 pour
            toujours (bug vu en prod le 2026-08-24). Les prix, ça s'affiche
            toujours, sans animation. ─────────────────────────────────────── */}
      {pricingTiers.length > 0 && (
        <section id="tarifs" className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "#B8962E" }}>Tarifs</p>
              <h2 className="text-3xl font-light text-stone-900 tracking-tight mb-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Un plan pour chaque communauté
              </h2>
              <p className="text-stone-500 text-sm max-w-xl mx-auto">
                Commencez gratuitement, évoluez quand votre communauté grandit.
                Sans engagement — 2 mois offerts en paiement annuel.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
              {pricingTiers.map(t => (
                <div key={t.id}
                  className={`relative flex flex-col rounded-3xl p-6 border transition-all hover:shadow-[0_18px_40px_-18px_rgba(13,27,51,0.18)] ${
                    t.recommended ? "border-[#B8962E] shadow-[0_14px_34px_-16px_rgba(184,150,46,0.35)]" : "border-[#E9E1D2]"
                  }`}
                  style={{ background: t.recommended ? "linear-gradient(160deg,#FFFDF6,#FCF6E8)" : "#FCFAF4" }}>
                  {t.recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.18em] text-white px-3 py-1 rounded-full font-semibold"
                      style={{ background: "#B8962E" }}>
                      Recommandé
                    </div>
                  )}
                  <p className="text-lg font-medium text-stone-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t.name}</p>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-3xl font-semibold text-stone-900">
                      {t.price_monthly_cents === 0 ? "Gratuit" : `${Math.round(t.price_monthly_cents / 100)} €`}
                    </span>
                    {t.price_monthly_cents > 0 && <span className="text-xs text-stone-500">HT /mois</span>}
                  </div>
                  {t.description && <p className="text-xs text-stone-500 mt-2 leading-relaxed">{t.description}</p>}
                  <ul className="mt-5 space-y-2 text-[13px] text-stone-700 flex-1">
                    {(t.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: "#B8962E" }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact?type=org"
                    className="mt-6 text-center text-sm font-medium px-4 py-2.5 rounded-full transition-colors hover:opacity-90"
                    style={t.recommended
                      ? { background: "#0D1B33", color: "#FFFFFF" }
                      : { border: "1.5px solid #0D1B33", color: "#0D1B33" }}>
                    {t.price_monthly_cents === 0 ? "Commencer gratuitement" : "Choisir ce plan"}
                  </Link>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-stone-400 mt-8">
              Prix HT. Reçus Cerfa, espace donateur et saisie Post-Chabat inclus dans tous les plans.
            </p>
          </div>
        </section>
      )}

      {/* ── Footer — PANNEAU SOMBRE qui lévite : la page se referme sur le navy
            immersif (remplace le fondu crème→navy). ──────────────────────── */}
      <footer
        className="relative z-40 -mt-8 md:-mt-12 rounded-t-[32px] md:rounded-t-[48px] overflow-hidden"
        style={{ background: "#0D1B33", borderTop: "1.5px solid rgba(184,150,46,0.55)", boxShadow: "0 -40px 80px -36px rgba(13,27,51,0.5)" }}>
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            <div className="md:col-span-2">
              <TeroumaLogo size={40} variant="full" onDark />
              <p className="text-stone-400 text-sm mt-3 leading-relaxed max-w-xs">
                La plateforme de gestion des dons pour les communautés juives de France. Conforme Cerfa, sécurisé, simple.
              </p>
              <p className="mt-4 text-[11px]" style={{ color: "#B8962E", fontFamily: "serif", fontSize: "18px" }}>
                תרומה
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-stone-400 mb-4">Plateforme</p>
              <ul className="space-y-2 text-sm text-stone-400">
                {[
                  { label: "Organisations", href: "#organisations" },
                  { label: "Campagnes", href: "#campagnes" },
                  { label: "Tarifs", href: "#tarifs" },
                  { label: "Kiddoush", href: "/kiddoush" },
                  { label: "Mon espace donateur", href: "/mon-espace" },
                  { label: "Nous contacter", href: "/contact" },
                  { label: "Connexion", href: "/login" },
                ].map(l => (
                  <li key={l.label}><Link to={l.href} className="hover:text-white transition-colors">{l.label}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-stone-400 mb-4">Contact</p>
              <ul className="space-y-2 text-sm text-stone-400">
                <li className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" />contact@terouma.fr</li>
                <li className="flex items-center gap-2"><Globe className="h-3.5 w-3.5" />terouma.fr</li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-6 flex flex-col md:flex-row justify-between gap-3" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
            <p className="text-xs text-stone-500">
              © 2025 Terouma, édité par L.I.A · <Link to="/mentions-legales" className="underline hover:text-white transition-colors">Mentions légales</Link>
            </p>
            <p className="text-xs text-stone-500">Réduction d'impôt 66% · Article 200 CGI · Cerfa 11580*03</p>
          </div>
        </div>
      </footer>

      {/* ── Donate modal ──────────────────────────────────────────────── */}
      {donateModal && (
        <DonateModal org={donateModal} onClose={() => setDonateModal(null)} />
      )}
    </div>
  );
}

/* ─── Impact fiscal (slider) ────────────────────────────────────────────── */
function TaxSlider() {
  const AMOUNTS = [50, 100, 200, 500, 1000, 2000, 5000];
  const [idx, setIdx] = useState(1);
  const [type, setType] = useState("particulier"); // particulier | societe
  const [income, setIncome] = useState("");

  const amt = AMOUNTS[idx];
  const incomeAmt = parseFloat(income) || 0;
  const rate = type === "particulier" ? 0.66 : 0.60;
  const capPct = type === "particulier" ? 0.20 : 0.005;
  const cap = incomeAmt > 0 ? incomeAmt * capPct : Infinity;
  const rawReduction = Math.round(amt * rate);
  const reduction = Math.min(rawReduction, Math.round(cap));
  const netCost = amt - reduction;
  const pct = Math.round((reduction / amt) * 100);
  const capped = incomeAmt > 0 && rawReduction > Math.round(cap);

  return (
    <section className="py-20 px-6" style={{ background: "#0D1B33" }}>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "#B8962E" }}>Art. 200 & 238 bis CGI</p>
          <h2 className="text-3xl font-light text-white mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Votre don, votre impact réel
          </h2>
          <p className="text-stone-400 text-sm max-w-lg mx-auto">
            En France, une grande partie de votre don est remboursée sur vos impôts. Calculez votre coût réel.
          </p>
        </div>

        <div className="rounded-3xl p-8" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)" }}>
          {/* Type toggle */}
          <div className="flex gap-2 mb-6">
            {[{ v: "particulier", l: "Particulier · 66%" }, { v: "societe", l: "Société · 60%" }].map(t => (
              <button key={t.v} type="button" onClick={() => setType(t.v)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors"
                style={{ background: type === t.v ? "#B8962E" : "rgba(255,255,255,0.07)", color: type === t.v ? "#fff" : "rgba(255,255,255,0.5)" }}>
                {t.l}
              </button>
            ))}
          </div>

          {/* Amount display + slider */}
          <div className="text-center mb-6">
            <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-2">Je donne</p>
            <p className="text-4xl sm:text-6xl font-bold text-white mb-1 break-words">{amt.toLocaleString("fr-FR")} €</p>
          </div>
          <div className="mb-6">
            <input type="range" min={0} max={AMOUNTS.length - 1} step={1} value={idx}
              onChange={e => setIdx(Number(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{ background: `linear-gradient(to right, #B8962E ${(idx / (AMOUNTS.length - 1)) * 100}%, rgba(255,255,255,0.2) ${(idx / (AMOUNTS.length - 1)) * 100}%)`, outline: "none" }} />
            <div className="flex justify-between text-[10px] text-stone-500 mt-2">
              {AMOUNTS.map(a => <span key={a}>{a >= 1000 ? `${a/1000}k` : a}€</span>)}
            </div>
          </div>

          {/* Income input */}
          <div className="mb-6">
            <label className="block text-xs text-stone-400 mb-2">
              {type === "particulier" ? "Revenu imposable (€) — optionnel, pour calculer le plafond 20%" : "Chiffre d'affaires HT (€) — optionnel, pour calculer le plafond 0,5%"}
            </label>
            <input type="number" min="0" value={income} onChange={e => setIncome(e.target.value)}
              placeholder={type === "particulier" ? "ex : 35 000" : "ex : 500 000"}
              className="w-full px-4 py-3 rounded-xl text-right font-mono text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
              style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }} />
          </div>

          {/* Results */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="text-center rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.05)" }}>
              <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-2">Don total</p>
              <p className="text-xl font-bold text-white">{amt.toLocaleString("fr-FR")} €</p>
            </div>
            <div className="text-center rounded-2xl p-4" style={{ background: "rgba(184,150,46,0.15)", border: "1px solid rgba(184,150,46,0.3)" }}>
              <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: "#B8962E" }}>Réduction impôts</p>
              <p className="text-xl font-bold" style={{ color: "#B8962E" }}>- {reduction.toLocaleString("fr-FR")} €</p>
              {capped && <p className="text-[9px] text-stone-400 mt-1">Plafond · reste reportable 5 ans</p>}
            </div>
            <div className="text-center rounded-2xl p-4" style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)" }}>
              <p className="text-[10px] text-emerald-300 uppercase tracking-wider mb-2">Coût réel</p>
              <p className="text-xl font-bold text-emerald-300">{Math.max(0, netCost).toLocaleString("fr-FR")} €</p>
            </div>
          </div>

          <div className="rounded-xl p-4" style={{ background: "rgba(0,0,0,0.2)" }}>
            <div className="flex justify-between text-xs text-stone-400 mb-2">
              <span>Part remboursée par l'État</span>
              <span className="font-bold" style={{ color: "#B8962E" }}>{pct}%</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, background: "linear-gradient(90deg, #B8962E, #e8c97a)" }} />
            </div>
            <p className="text-[11px] text-stone-500 mt-2 text-center">
              {type === "particulier" ? "Taux 66% · Plafond 20% du revenu · Art. 200 CGI" : "Taux 60% · Plafond 0,5% du CA · Art. 238 bis CGI"}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Donate modal ──────────────────────────────────────────────────────── */
function DonateModal({ org, onClose }) {
  return (
    <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="text-center mb-6">
          <div className="h-14 w-14 rounded-2xl flex items-center justify-center text-lg font-bold text-white mx-auto mb-3"
            style={{ background: "linear-gradient(135deg, #0D1B33 0%, #1B2A4A 100%)" }}>
            {org.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <h3 className="text-xl font-semibold text-stone-900">{org.name}</h3>
          <div className="flex items-center justify-center gap-1 mt-1">
            <MapPin className="h-3.5 w-3.5 text-stone-400" />
            <span className="text-sm text-stone-500">{org.city}</span>
          </div>
        </div>

        <div className="bg-stone-50 rounded-2xl p-4 mb-6 text-sm text-stone-600 text-center">
          Pour faire un don à cette organisation, connectez-vous ou créez un compte gestionnaire.
          <br />Le donateur reçoit un lien personnel par email ou SMS.
        </div>

        <div className="space-y-3">
          <Link to="/login"
            className="block w-full py-3 text-center rounded-xl font-medium text-white text-sm transition-colors"
            style={{ background: "#0D1B33" }}
            onMouseEnter={e => e.currentTarget.style.background = "#1B2A4A"}
            onMouseLeave={e => e.currentTarget.style.background = "#0D1B33"}>
            Accéder à l'espace gestion
          </Link>
          <button onClick={onClose} className="block w-full py-3 text-center rounded-xl text-sm text-stone-500 hover:text-stone-900 transition-colors">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Demo data (shown when DB is empty) ────────────────────────────────── */
const DEMO_ORGS = [
  { id: "d1", name: "Synagogue Paris Centre",   city: "Paris 4e",      donor_count: 247, total_collected: 84200 },
  { id: "d2", name: "Synagogue Neuilly",         city: "Neuilly-sur-Seine", donor_count: 183, total_collected: 62100 },
  { id: "d3", name: "Synagogue Lyon Confluence", city: "Lyon 2e",       donor_count: 134, total_collected: 41800 },
  { id: "d4", name: "Synagogue Victoire",          city: "Paris 9e",      donor_count: 312, total_collected: 97500 },
  { id: "d5", name: "Synagogue Marseille",        city: "Marseille 13e", donor_count: 98,  total_collected: 28300 },
  { id: "d6", name: "Synagogue Strasbourg",       city: "Strasbourg",    donor_count: 87,  total_collected: 24700 },
  { id: "d7", name: "Synagogue Nice Côte d'Azur", city: "Nice",          donor_count: 76,  total_collected: 19800 },
  { id: "d8", name: "Synagogue Bordeaux",         city: "Bordeaux",      donor_count: 65,  total_collected: 15200 },
];

const DEMO_STATS = { organizations: 47, donors: 2400, total_collected: 387000, campaigns: 12 };

// DEMO_CAMPAIGNS removed — we now show ONLY real campaigns to avoid
// confusing admins whose drafts don't appear. If no campaign is published,
// the campaigns section is hidden entirely.
